import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchRecurringInvoiceById, deleteRecurringInvoice } from '../utils/api';
import { formatDate } from '../utils/date';
import './InvoiceDetailModal.css';
import './Invoices.css';
import './_Buttons.css';

const RecurringInvoiceDetail = () => {
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        const getInvoice = async () => {
            try {
                const response = await fetchRecurringInvoiceById(id);
                setInvoice(response.data);
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };
        getInvoice();
    }, [id]);

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this recurring invoice?')) {
            try {
                await deleteRecurringInvoice(id);
                navigate('/recurring-invoices');
            } catch (error) {
                setError(error.message);
            }
        }
    };

    const formatAmount = (amount) => {
        return ((amount || 0) / 100).toFixed(2);
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    if (!invoice) {
        return <div>No invoice found</div>;
    }

    return (
        <div className="student-detail-container">
            <button onClick={() => navigate('/recurring-invoices')} className="back-button text-button">Go Back</button>
            <div className="modal-content-page">
                <h2>Recurring Invoice Details</h2>
                <div className="invoice-details-grid">
                    <div className="invoice-details-section">
                        <h3>{invoice.name}</h3>
                        <div className="detail-item"><h4>{invoice.student.first_name} {invoice.student.last_name}</h4></div>
                        <div className="detail-item">{invoice.student.email}</div>
                        {invoice.description && <div className="detail-item"><strong>Description:</strong> {invoice.description}</div>}
                        <div className="detail-item"><strong>First Invoice Date:</strong> {formatDate(invoice.first_invoice_date)}</div>
                        {invoice.end_date && <div className="detail-item"><strong>End Date:</strong> {formatDate(invoice.end_date)}</div>}
                        <div className="detail-item"><strong>Interval:</strong> {invoice.interval === 1 ? `Every ${invoice.interval_type.name.slice(0, -1)}` : `Every ${invoice.interval} ${invoice.interval_type.name}`}</div>
                        <div className="detail-item"><strong>Due Days:</strong> {invoice.due_days}</div>
                    </div>
                    <div className="invoice-details-section">
                        <h3>Fees & Discounts</h3>
                        {invoice.invoice_fees.invoice_fees.map(fee => (
                            <div key={fee.id} className="detail-item">
                                <span>{fee.name} ({fee.quantity} x ${formatAmount(fee.amount)})</span>
                                <span>${formatAmount(fee.amount * fee.quantity)}</span>
                            </div>
                        ))}
                        {invoice.invoice_discounts.invoice_discounts.map(discount => (
                            <div key={discount.id} className="detail-item">
                                <span>
                                    {discount.name}{' '}
                                    {discount.type === 'Fixed'
                                        ? `(-$${formatAmount(discount.amount)})`
                                        : `(${discount.amount / 100}%)`}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="amounts-section">
                    <div className="amount-item">
                        <strong>Total Fees:</strong>
                        <span>${formatAmount(invoice.invoice_fees.total_amount)}</span>
                    </div>
                    <div className="amount-item">
                        <strong>Total Discounts:</strong>
                        <span>-${formatAmount(invoice.invoice_discounts.total_amount)}</span>
                    </div>
                    <div className="amount-item">
                        <strong className='total-amount'>Total Amount:</strong>
                        <span className='total-amount'>${formatAmount(invoice.total_amount)}</span>
                    </div>
                </div>

                {invoice.payment_link && (
                    <p><a href={invoice.payment_link} target="_blank" rel="noopener noreferrer">Payment Link</a></p>
                )}
                
                <div className='actions-centered'>
                    <button onClick={handleDelete} className='delete-button text-button'>Delete</button>
                </div>
            </div>
        </div>
    );
};

export default RecurringInvoiceDetail;
